import { useEffect, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { draftToQuery, EMPTY_DRAFT, type MissionChoice } from '../../lib/startDraft.ts';
import { CookieSettingsLink } from '../../components/CookieBanner.tsx';
import {
  APP_PATH,
  BRAND,
  CONTACT_EMAIL,
  HOTJAR_ANSWER,
  PRICING,
  PROMO_VIDEO,
  realVisitorsLink,
  SHOW_PROMO_VIDEO,
  rememberPlan,
  signUpPath,
} from '../../lib/brand.ts';
import { REAL_USERS_PUBLIC } from '../../lib/features.ts';
import { usePageMeta } from '../../lib/public.ts';
import { trackSite } from '../../lib/siteEvents.ts';
import { FINAL, HOW, INCLUDED, PROBLEM, REAL, SPRITE, STEPS } from './landing/markup.ts';

/**
 * The home page (design "daevio — landing editoriale", prototype in static/daevio): the offer and the test form,
 * the film next to a visit told in four beats, the four steps, the problem, the real visitors, plans, questions and
 * the last call. Decorative sections come from the prototype as HTML (landing/markup.ts); everything that leads
 * somewhere (the form, sign-up, plans, legal pages) is React and uses the app's own routes and data.
 * The pictures are made-up examples, never a real site.
 */

const FOUNDER = 'Founder price, locked forever';

/** Where a test starts: sign-up (or sign-in) first, then the new test with the site (read at once) and the mission. */
const startPath = (query: string) => signUpPath(`${APP_PATH}/new?${query}`);

/** The mission picker of the hero, in the order of the design. */
const MISSIONS: { label: string; choice: MissionChoice }[] = [
  { label: 'Just browse', choice: 'browse' },
  { label: 'Sign up', choice: 'signup' },
  { label: 'Find the price', choice: 'pricing' },
  { label: 'Buy', choice: 'buy' },
  { label: 'Something else', choice: 'custom' },
];

/** The visitors a free test can have at once. */
const FREE_VISITORS = 3;

/**
 * The visitor chips under the form: friendly, everyday people from the ready-made library (the tougher ones are
 * for a later run). Three at a time; the choice travels to the start page with the site.
 */
const PEOPLE: { key: string; orb: string; name: string; full: string; say: string; on?: true }[] = [
  { key: 'curious-browser', orb: 'o4', name: 'Curious', full: 'Curious window-shopper', say: '“Oh, what is this? Let me look around.”', on: true },
  { key: 'early-adopter', orb: 'o2', name: 'Early bird', full: 'Early adopter', say: '“New tool? Let me try it right now.”', on: true },
  { key: 'loyal-fan', orb: 'o6', name: 'Fan', full: 'Long-time fan', say: '“I love this brand. What is new?”', on: true },
  { key: 'word-of-mouth', orb: 'o1', name: 'Referred', full: 'Friend recommendation', say: '“A friend swore by it. Let me see what the fuss is about.”' },
  { key: 'tablet-evening', orb: 'o5', name: 'Relaxed', full: 'Relaxed evening browser', say: '“I have time tonight. Show me the pictures.”' },
  { key: 'student-explorer', orb: 'o3', name: 'Student', full: 'Student exploring', say: '“Is there a student plan?”' },
];

const FAQ: { q: string; a: string; more?: true }[] = [
  {
    q: 'Do I need to install anything to test my site?',
    a: 'No. The AI visitors open your site in a real browser of ours, from its public address. The script is only for recording your real visitors, if you want that too.',
  },
  {
    q: 'Are forms really sent, or things bought?',
    a: 'Never. Forms are intercepted before they reach your site, and payment providers are blocked. Your data and your stock stay as they are.',
  },
  {
    q: 'What is a visit?',
    a: 'One AI visitor going through your site once. A test with five visitors uses five visits. Visits renew every month and do not pile up.',
  },
  { q: 'Is this like Hotjar or Microsoft Clarity?', a: HOTJAR_ANSWER, more: true },
  {
    q: 'What about my visitors’ privacy?',
    a: 'Everything typed in forms is masked, no IP address is stored, recordings are deleted after 30 days, and the script can wait for your cookie banner. You decide, as the site owner, and we process the data on your behalf.',
    more: true,
  },
  {
    q: 'Can I cancel?',
    a: 'Any time, from the billing portal. Your plan stays active until the end of the month you paid for.',
  },
];

/** The prototype's HTML with the app's own links filled in. */
const fill = (html: string) => html.replaceAll('__SIGNUP__', signUpPath()).replaceAll('__REAL__', realVisitorsLink('landing'));

/** The first number (or "Unlimited", "Founder price") of a plan line, in bold. */
function emphasise(line: string): ReactNode {
  const m = /(Founder price|Unlimited|\d[\d,]*)/.exec(line);
  if (!m) return line;
  return (
    <>
      {line.slice(0, m.index)}
      <b>{m[0]}</b>
      {line.slice(m.index + m[0].length)}
    </>
  );
}

/** A plan's lines: the sites first, then (once real visitors are public) its real sessions, then the rest. */
function planLines(p: (typeof PRICING)[number]): string[] {
  const [sites, ...rest] = p.items as readonly string[];
  return REAL_USERS_PUBLIC ? [sites!, p.sessions, ...rest] : [sites!, ...rest];
}

function Logo() {
  return (
    <svg className="logo__mark" viewBox="0 0 32 32" aria-hidden="true">
      <use href="#mk" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg className="arrow" aria-hidden="true">
      <use href="#arw" />
    </svg>
  );
}

function Header() {
  return (
    <>
      <header className="nav-wrap" data-nav>
        <div className="container">
          <nav className="nav" aria-label="Main">
            <div className="nav__bg" />
            <Link to="/" className="logo" aria-label={`${BRAND} home`}>
              <Logo />
              {BRAND}
            </Link>
            <ul className="nav__menu">
              <li>
                <a href="#how">How it works</a>
              </li>
              <li>
                <a href="#pricing">Pricing</a>
              </li>
              <li>
                <a href="#faq">FAQ</a>
              </li>
              <li>
                <Link to="/docs">Docs</Link>
              </li>
            </ul>
            <div className="nav__btns">
              <Link to={APP_PATH} className="btn btn-secondary nav__in">
                Sign in
              </Link>
              <a href="#start" className="btn btn-secondary nav__test" data-focus-site>
                Test my site free
              </a>
              <Link to={signUpPath()} className="btn btn-primary">
                Sign up <ArrowIcon />
              </Link>
            </div>
            <button type="button" className="burger" aria-label="Open menu" data-menu-open>
              <svg aria-hidden="true">
                <use href="#menu" />
              </svg>
            </button>
          </nav>
        </div>
      </header>
      <div className="mmenu" data-menu>
        <div className="mmenu__head">
          <Link to="/" className="logo">
            <Logo />
            {BRAND}
          </Link>
          <button type="button" className="burger" style={{ margin: 0 }} aria-label="Close menu" data-menu-close>
            <svg aria-hidden="true">
              <use href="#close" />
            </svg>
          </button>
        </div>
        <a href="#how" className="item">
          How it works
        </a>
        <a href="#pricing" className="item">
          Pricing
        </a>
        <a href="#faq" className="item">
          FAQ
        </a>
        <Link to="/docs" className="item">
          Docs
        </Link>
        <div className="btn-group">
          <Link to={APP_PATH} className="btn btn-secondary">
            Sign in
          </Link>
          <Link to={signUpPath()} className="btn btn-primary">
            Sign up
          </Link>
        </div>
      </div>
    </>
  );
}

function Hero() {
  const navigate = useNavigate();
  const url = useRef<HTMLInputElement>(null);
  const mission = useRef<HTMLSelectElement>(null);
  const people = useRef<HTMLDivElement>(null);
  const [missing, setMissing] = useState(false);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const site = url.current?.value.trim() ?? '';
    if (site.length < 3) {
      // The field shakes and turns red (behaviour.ts); the line under the form says what is missing.
      setMissing(true);
      url.current?.focus();
      return;
    }
    const choice = (mission.current?.value as MissionChoice | undefined) ?? 'browse';
    const keys = [...(people.current?.querySelectorAll<HTMLElement>('[data-p].is-on') ?? [])]
      .map((b) => b.dataset.key ?? '')
      .filter(Boolean)
      .slice(0, FREE_VISITORS);
    const query = draftToQuery({ ...EMPTY_DRAFT, url: site, choice });
    navigate(startPath(keys.length ? `${query}&personas=${keys.join(',')}` : query));
  };
  return (
    <section className="section hero" id="start" aria-labelledby="hero-title">
      <div className="hero__bg" aria-hidden="true">
        <span className="line" style={{ top: 0 }} />
        <canvas className="s-hero-orb" data-hero-orb />
      </div>
      <div className="container hero__main s-hero">
        <div className="s-hero__grid">
          <div className="s-hero__title">
            <p className="s-eyebrow">
              <span className="s-eyebrow__orbs" aria-hidden="true">
                <i className="d-orb o2" />
                <i className="d-orb o1" />
                <i className="d-orb o3" />
              </span>
              AI visitors with a persona, in a real browser
            </p>
            <h1 id="hero-title">
              <em className="main">
                Watch AI visitors use your site <span className="s-hl">before your customers do.</span>
              </em>
            </h1>
            <p className="s-sub">
              Three visitors with a persona open your site in a real browser and think out loud. You get every recording, what got in their way, and a prompt to fix it.
            </p>
          </div>
          <div className="s-cardwrap">
            {/* An account is needed to start a test: the site and mission travel through sign-up (or sign-in). */}
            <form className="s-cmd" data-form noValidate onSubmit={submit} aria-label="Test your site">
              <div className="s-cmd__bar">
                <div className="s-cmd__url" data-url>
                  <svg aria-hidden="true">
                    <use href="#globe" />
                  </svg>
                  <label className="sr-only" htmlFor="site">
                    Your site
                  </label>
                  <input
                    ref={url}
                    id="site"
                    type="text"
                    inputMode="url"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="yoursite.com"
                    autoComplete="url"
                    aria-invalid={missing || undefined}
                    aria-describedby={missing ? 'site-missing' : undefined}
                    onInput={() => setMissing(false)}
                  />
                </div>
                <label className="s-cmd__mission">
                  <span>Mission</span>
                  <span className="s-cmd__sel">
                    <select ref={mission} id="mission" defaultValue="browse">
                      {MISSIONS.map((m) => (
                        <option key={m.choice} value={m.choice}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                      <use href="#chev" />
                    </svg>
                  </span>
                </label>
                <button type="submit" className="btn btn-primary s-cmd__go">
                  Test my site free <ArrowIcon />
                </button>
              </div>
              <div className="s-cmd__who" data-who>
                <span className="s-cmd__lbl">
                  Who visits{' '}
                  <span>
                    <b data-count>3</b>/{FREE_VISITORS}
                  </span>
                </span>
                <div className="s-cmd__people" role="group" aria-label={`Choose who visits, up to ${FREE_VISITORS}`} ref={people} data-max={FREE_VISITORS}>
                  {PEOPLE.map((p) => (
                    <button
                      key={p.full}
                      type="button"
                      className={p.on ? 's-chip is-on' : 's-chip'}
                      data-p
                      data-key={p.key}
                      data-say={p.say}
                      aria-pressed={p.on ? 'true' : 'false'}
                      title={p.full}
                    >
                      <span className={`d-orb ${p.orb}`} />
                      <span className="v-p__name">{p.name}</span>
                    </button>
                  ))}
                </div>
                <button type="button" className="s-cmd__change" data-change>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <use href="#i-repeat" />
                  </svg>
                  Change
                </button>
              </div>
              {missing && (
                <p className="s-cmd__missing" id="site-missing" role="alert">
                  Type your site’s address first, then press Test my site free.
                </p>
              )}
              <p className="s-cmd__note">No card · about 5 minutes · nothing to install, nothing is really sent or bought</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing({ faq }: { faq: { q: string; a: ReactNode }[] }) {
  const included = REAL_USERS_PUBLIC ? INCLUDED : INCLUDED.replace(/<li>(?:(?!<li>).)*heatmaps of your real visitors<\/li>/, '');
  return (
    <section className="section d-pricing" id="pricing" aria-labelledby="pricing-title">
      <div className="container rows g64">
        <div className="s-head s-pr-head">
          <p className="f-eyebrow">Pricing</p>
          <h2 className="s-title" id="pricing-title">
            Free to try. <span className="s-hl">Pay for visits when it becomes a habit.</span>
          </h2>
        </div>
        <div className="d-plans3 s-prs" role="list" aria-label="Plans">
          {PRICING.map((p, i) => {
            const featured = i === 1;
            const founder = (p.items as readonly string[]).includes(FOUNDER);
            const [n, ...visits] = p.visits.split(' ');
            return (
              <div key={p.name} role="listitem" className={featured ? 'd-price s-pr is-featured' : 'd-price s-pr'}>
                <span className="bcard__border" />
                <div className="d-price__inner">
                  <div className="s-pr__top">
                    <h3 className="s-pr__name">
                      {p.name}
                      {founder && <span className="d-tag">Founder price</span>}
                    </h3>
                    <p className="s-pr__amt">
                      <b>{p.price}</b>
                      {'plan' in p && <span>{p.note}</span>}
                    </p>
                  </div>
                  <div className="s-pr__visits">
                    <span>
                      <b>{n}</b> {visits.join(' ')}
                    </span>
                  </div>
                  <ul className="d-list s-pr__list">
                    {planLines(p).map((line) => (
                      <li key={line}>
                        <span>{emphasise(line)}</span>
                      </li>
                    ))}
                    {'custom' in p && (
                      <li>
                        <span>
                          {p.custom}{' '}
                          {CONTACT_EMAIL ? <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`${BRAND} Enterprise`)}`}>Write to us.</a> : 'Write to us.'}
                        </span>
                      </li>
                    )}
                  </ul>
                  {'plan' in p ? (
                    <Link
                      className={featured ? 'btn btn-primary' : 'btn btn-secondary'}
                      to={signUpPath(undefined, p.plan)}
                      onClick={() => {
                        trackSite({ name: 'checkout_clicked', product: p.plan });
                        if (p.plan) rememberPlan(p.plan);
                      }}
                    >
                      {p.cta} {featured && <ArrowIcon />}
                    </Link>
                  ) : (
                    <a href="#start" className="btn btn-secondary" data-focus-site>
                      Test my site free
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="s-incl" dangerouslySetInnerHTML={{ __html: included.replace(/^<div class="s-incl">|<\/div>$/g, '') }} />
        <div className="glow-divider" />
        <section className="rows g48 s-faq2" id="faq" aria-labelledby="faq-title">
          <header className="grid12 sheader">
            <div className="sheader__primary">
              <h2 className="s-title" id="faq-title">
                Questions
              </h2>
            </div>
          </header>
          <div className="story-list">
            {faq.map((f, i) => (
              <div key={f.q} className={i === 0 ? 'story s-fq is-open' : 'story s-fq'}>
                <div className="story__summary">
                  <span className="story__logo s-fq__n">{String(i + 1).padStart(2, '0')}</span>
                  <button type="button" className="story__btn" data-story-toggle aria-expanded={i === 0 ? 'true' : 'false'}>
                    <h3 className="h-sm">{f.q}</h3>
                  </button>
                  <div className="story__action">
                    <button type="button" className="story__plus" data-story-toggle aria-label="Open" tabIndex={-1}>
                      <svg aria-hidden="true">
                        <use href="#plus" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="story__content">
                  <div>
                    <div className="s-fq__a t-lg soft">
                      <p>{f.a}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="s-faq-docs t-md soft">
            Didn't find your answer? <Link to="/docs">The docs</Link> explain every feature step by step, starting with{' '}
            <Link to="/docs/quickstart">your first test in five minutes</Link>.
          </p>
        </section>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="section footer">
      <div className="container">
        <div className="flinks d-flinks f-flinks">
          <div className="fcol d-fbrand">
            <Link to="/" className="logo" aria-label={`${BRAND} home`}>
              <Logo />
              {BRAND}
            </Link>
            <p className="t-md">AI visitors test your site before your users do.</p>
          </div>
          <div className="fcol">
            <nav className="fblock" aria-label="Product">
              <h4>Product</h4>
              <a href="#how">How it works</a>
              {REAL_USERS_PUBLIC && <Link to={realVisitorsLink('footer')}>Real visitors</Link>}
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
              <Link to="/docs">Docs</Link>
            </nav>
          </div>
          <div className="fcol">
            <nav className="fblock" aria-label="Legal">
              <h4>Legal</h4>
              <Link to="/terms">Terms</Link>
              <Link to="/privacy">Privacy</Link>
              <Link to="/cookies">Cookies</Link>
              <Link to="/dpa">DPA</Link>
              <Link to="/subprocessors">Sub-processors</Link>
              <CookieSettingsLink />
            </nav>
          </div>
        </div>
        <div className="fbottom">
          <div />
          <svg className="d-mark" viewBox="0 0 32 32" aria-hidden="true">
            <use href="#mk" />
          </svg>
        </div>
      </div>
    </footer>
  );
}

/** The film, opened from "See daevio in action". */
function Film({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="dvl-film" role="dialog" aria-modal="true" aria-label={`${BRAND} in action`} onClick={onClose}>
      <div className="dvl-film__box" onClick={(e) => e.stopPropagation()}>
        <video src={PROMO_VIDEO.src} poster={PROMO_VIDEO.poster} controls autoPlay playsInline />
        <button type="button" className="dvl-film__close" onClick={onClose} aria-label="Close the film">
          ×
        </button>
      </div>
    </div>
  );
}

export function LandingPage() {
  usePageMeta(`${BRAND}: test your site with AI visitors before your users do`);
  const location = useLocation();
  const navigate = useNavigate();
  const root = useRef<HTMLDivElement>(null);
  const [film, setFilm] = useState(false);

  // The behaviour (and GSAP) loads with the home page only, in its own chunk.
  useEffect(() => {
    let dispose: (() => void) | undefined;
    let gone = false;
    void import('./landing/behaviour.ts').then(({ initLanding }) => {
      if (!gone && root.current) dispose = initLanding(root.current);
    });
    return () => {
      gone = true;
      dispose?.();
    };
  }, []);

  useEffect(() => {
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, [location.hash]);

  /** Links inside the prototype's HTML go through the router; the film opens in place. */
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest('a');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (a.hasAttribute('data-video')) {
      e.preventDefault();
      if (SHOW_PROMO_VIDEO) setFilm(true);
      else document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const href = a.getAttribute('href') ?? '';
    if (href.startsWith('/') && !href.startsWith('//') && !a.target) {
      e.preventDefault();
      navigate(href);
    }
  };

  const faq = (REAL_USERS_PUBLIC ? FAQ : FAQ.filter((f) => !/Hotjar|privacy/.test(f.q))).map((f) => ({
    q: f.q,
    a: f.more ? (
      <>
        {f.a} <Link to={realVisitorsLink('faq')}>More on how the script works.</Link>
      </>
    ) : (
      f.a
    ),
  }));

  return (
    <div className="dvl" ref={root} onClick={onClick}>
      {/* The icons' symbols: out of sight but rendered (gradients inside a hidden SVG do not paint). */}
      <div aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: SPRITE }} />
      <Header />
      <main>
        <Hero />
        <section className="section" id="how" aria-labelledby="how-title" dangerouslySetInnerHTML={{ __html: fill(HOW) }} />
        <section className="section steps-sec" id="steps" dangerouslySetInnerHTML={{ __html: STEPS }} />
        <section className="section biz f-problem" id="problem" dangerouslySetInnerHTML={{ __html: PROBLEM }} />
        {REAL_USERS_PUBLIC && (
          <section className="section dark rv-ed" id="real-visitors" aria-labelledby="real-title" dangerouslySetInnerHTML={{ __html: fill(REAL) }} />
        )}
        <Pricing faq={faq} />
        <section className="section dark s-fcta2" aria-label="Start" dangerouslySetInnerHTML={{ __html: fill(FINAL) }} />
      </main>
      <Footer />
      <div className="m-cta" data-mcta>
        <a href="#start" className="btn btn-primary" data-focus-site>
          Test my site free <ArrowIcon />
        </a>
        <Link to={signUpPath()} className="btn btn-secondary">
          Sign up
        </Link>
      </div>
      <Film open={film} onClose={() => setFilm(false)} />
    </div>
  );
}
